import { useAccountStore } from "@/features/account-management";
import { useDeleteSessionsMutation } from "@/shared/services";
import Button from "@components/ui/button";
import {
  Card,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import Spinner from "@components/ui/spinner";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import moment from "moment";
import { useCallback } from "react";
import { toast } from "react-toastify";
import useGetQueryData from "../hooks/use-get-query-data";

const ID = "are-you-sure-dialog";
const dateTo = moment().add(1, "day").format("YYYY-MM-DD");

export const AreYouSureDialog = ({
  versionId,
  sessionId,
}:
  | { versionId: string; sessionId?: string }
  | { versionId?: string; sessionId: string }) => {
  const showPlaceholder = useAccountStore((s) => s.showAnalyticsPlaceholder);

  const dispatch = useAppDispatch();
  const { mutateAsync: deleteSessions, isPending: isDeletingSessions } =
    useDeleteSessionsMutation();
  const { dateFrom } = useGetQueryData(true);
  const close = () => {
    dispatch(closeDialog(ID));
  };

  const handleSubmit = useCallback(async () => {
    if (isDeletingSessions) return;
    try {
      if (showPlaceholder) {
        toast.info("This action is not available in demo mode");
        close();
        return;
      }
      const params: {
        versionId?: string;
        sessionId?: string;
        dateFrom: string;
        dateTo: string;
      } = {
        dateFrom,
        dateTo,
      };
      if (versionId) params.versionId = versionId;
      if (sessionId) params.sessionId = sessionId;
      await deleteSessions(
        params as
          | { versionId: string; dateFrom: string; dateTo: string }
          | { sessionId: string; dateFrom: string; dateTo: string },
      );
    } catch {
      toast.error("Error deleting sessions");
    }
    close();
  }, [
    isDeletingSessions,
    deleteSessions,
    versionId,
    sessionId,
    close,
    showPlaceholder,
  ]);

  return (
    <Card className="tw-gap-0">
      <CardHeader className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
        <CardTitle>Are you sure?</CardTitle>
        <CardCloseButton onClick={close} />
      </CardHeader>
      <CardContent className="tw-text-blue-500 tw-text-sm tw-mb-4">
        <p>
          This action cannot be undone. <br /> This will permanently delete all
          sessions.
        </p>
      </CardContent>
      <CardFooter className="tw-justify-end tw-gap-2">
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          isLoading={isDeletingSessions}
        >
          {isDeletingSessions && (
            <Spinner className="tw-mr-1.5 tw-size-3 tw-border-white" />
          )}
          {isDeletingSessions
            ? "Deleting..."
            : showPlaceholder
              ? "Yes, delete (demo)"
              : "Yes, delete"}
        </Button>
      </CardFooter>
    </Card>
  );
};

AreYouSureDialog.id = ID;
